import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import FeatherIcon from './FeatherIcon';
import './Breadcrumb.css';

/**
 * The platform's one breadcrumb trail (DESIGN.md §4.7). Every page that shows a trail uses
 * this component, so the trail sits in one place and looks the same everywhere.
 *
 * Where it goes: first thing on the page, directly above the page's title block (under the
 * "New navigation" tab strip when the page sits in one). Pages built on PageHeader pass
 * `breadcrumbs` to it instead of placing this themselves.
 *
 * Props:
 *   items: Array<{ label, to?, onClick?, icon?, title? } | null | false>
 *     Empty entries (null, false, no label) are skipped, so a crumb that is not known yet can
 *     be written `name && { label: name }`. An entry with `onClick` is a button; one with
 *     `to` is a link; the last entry is the current page (plain text, aria-current="page",
 *     cut with an ellipsis when long); any other entry is plain text.
 *     `icon` is a Feather icon name drawn before the label.
 *   showBack: boolean (default false) — put a "Back" entry first that calls navigate(-1).
 *   aside: node — controls that share the trail's row, at its right (a date-range filter).
 *   className: extra class on the trail (or on the row when `aside` is given).
 *
 * Example:
 *   <Breadcrumb items={[
 *     { label: 'Atlas', to: '/agent-atlas/atlas' },
 *     { label: 'Agent fleet', to: '/agent-atlas/inventory' },
 *     agent && { label: agent.name },
 *   ]} />
 */

/** The "Back" entry: a separate component so useNavigate only runs when it is shown. */
const BackCrumb = () => {
  const navigate = useNavigate();
  return (
    <button type="button" className="breadcrumb-link" onClick={() => navigate(-1)}>
      <FeatherIcon name="arrow-left" size={12} className="breadcrumb-icon" />
      Back
    </button>
  );
};

const Breadcrumb = ({ items = [], showBack = false, aside = null, className = '' }) => {
  const { t } = useTranslation('commonUi');
  const shown = (items || []).filter((it) => it && it.label != null && it.label !== '');
  const count = shown.length + (showBack ? 1 : 0);

  const nav = (
    <nav
      className={`breadcrumb-row${className && !aside ? ` ${className}` : ''}`}
      aria-label={t('commonUi:commonBreadcrumb.breadcrumb')}
    >
      {showBack && (
        <>
          <BackCrumb />
          {count > 1 && <span className="breadcrumb-separator" aria-hidden="true">/</span>}
        </>
      )}
      {shown.map((it, i) => {
        const last = i === shown.length - 1;
        const icon = it.icon && <FeatherIcon name={it.icon} size={12} className="breadcrumb-icon" />;
        let node;
        if (it.onClick) {
          node = (
            <button type="button" className="breadcrumb-link" onClick={it.onClick} title={it.title || undefined}>
              {icon}{it.label}
            </button>
          );
        } else if (it.to && !last) {
          node = <Link to={it.to} className="breadcrumb-link" title={it.title || undefined}>{icon}{it.label}</Link>;
        } else if (last) {
          node = (
            <span className="breadcrumb-current" aria-current="page" title={it.title || String(it.label)}>
              {icon}{it.label}
            </span>
          );
        } else {
          node = <span className="breadcrumb-text" title={it.title || undefined}>{icon}{it.label}</span>;
        }
        return (
          <React.Fragment key={`${i}-${it.label}`}>
            {i > 0 && <span className="breadcrumb-separator" aria-hidden="true">/</span>}
            {node}
          </React.Fragment>
        );
      })}
    </nav>
  );

  if (!aside) return nav;
  return (
    <div className={`breadcrumb-bar${className ? ` ${className}` : ''}`}>
      {nav}
      <div className="breadcrumb-aside">{aside}</div>
    </div>
  );
};

export default Breadcrumb;
